import supabase from 'modules/core/supabase/supabase';
import { type IBroadcast, isNoRowsError } from 'modules/core/supabase/tables/broadcast';
import { setBroadcastState, setIsBroadcastInitialized } from 'modules/overlay-v2/store/overlay/overlay';
import { readinessTaskFailed, readinessTaskStarted, readinessTaskSucceeded } from 'modules/overlay-v2/store/readiness/readiness';
import { selectHasGoneLive, selectRetryToken } from 'modules/overlay-v2/store/readiness/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store/store';
import { toApiError } from 'shared/errors';

function useInitBroadcast() {
  const dispatch = useAppDispatch();
  const { deviceId } = useParams();
  const id = useMemo(() => (deviceId ? Number(deviceId) : undefined), [deviceId]);

  const [broadcast, setBroadcast] = useState<IBroadcast | undefined>(supabase.broadcast.data.value);

  // `retryToken` в зависимостях обязателен: без него сорвавшийся запрос трансляции
  // навсегда превращался в «Матч в приложении ещё не запущен» — оператору советовали
  // запустить уже идущий матч, а повтор до устройства не доходил.
  const retryToken = useAppSelector(selectRetryToken);

  useEffect(() => {
    if (!id) return;
    dispatch(setIsBroadcastInitialized(false));
    dispatch(readinessTaskStarted('device'));
    supabase.broadcast
      .fetchByDeviceId(id)
      .then((result) => {
        supabase.broadcast.data.value = result;
        dispatch(readinessTaskSucceeded('device'));
      })
      .catch((e) => {
        console.error(e);
        // `.single()` считает ошибкой и пустую выборку. «Трансляции нет» — это не сбой
        // связи, а обычное состояние: матч ещё не запустили. Красным помечаем только
        // настоящие отказы, иначе оператору советуют чинить то, что не сломано.
        if (isNoRowsError(e)) {
          supabase.broadcast.data.value = undefined;
          dispatch(readinessTaskSucceeded('device'));
          return;
        }
        // Прежнюю трансляцию не стираем: сорванный запрос ничего не говорит о том, идёт
        // ли матч. Раньше очистка стояла безусловно, и повтор посреди эфира обнулял
        // таймер, период и очередь событий.
        dispatch(readinessTaskFailed({ id: 'device', error: toApiError(e).message }));
      })
      .finally(() => {
        dispatch(setIsBroadcastInitialized(true));
      });

    supabase.broadcast.observeByDeviceId(id);

    return () => {
      supabase.broadcast.stopObserve();
    };
  }, [id, dispatch, retryToken]);

  // Приложение не обновляет строку трансляции, а удаляет её и вставляет заново: на тестовом
  // стенде в `broadcast` одна живая строка при `max(id) = 118`. Промежуток между `DELETE` и
  // `INSERT` — сотни миллисекунд, но раньше он читался как «матч сняли с устройства»:
  // `matchId` пропадал, а следом обнулялись период и таймер, гасло табло, снимался
  // `hasGoneLive` (возвращая полноэкранный чеклист) и передёргивалась подписка на журнал.
  // Замер `?diag=1` показал цену: 124 мс без строки → 1.5 с с погасшим табло и периодом «1».
  //
  // После выхода в эфир пропажу строки игнорируем: прежняя трансляция остаётся в силе до
  // прихода другого `matchId`. Цена решения — снятый в приложении матч не гасит табло до
  // смены матча или закрытия окна; принято осознанно (решение Р3 плана IMGM-1435).
  //
  // Латч читается из стора в момент события, а не из ref, который синхронизирует эффект:
  // между коммитом рендера с `hasGoneLive = true` и запуском пассивного эффекта есть окно,
  // и `DELETE`, пришедший в него, видел в ref ещё `false` — матч стирался уже в эфире.
  const store = useStore<RootState>();

  useEffect(() => {
    const callback = (newValue: IBroadcast | undefined) => {
      if (!newValue && selectHasGoneLive(store.getState())) {
        console.debug('broadcast', { event: 'row disappeared on air, keeping previous state' });
        return;
      }

      setBroadcast(newValue);
      dispatch(setBroadcastState(newValue));
      console.debug('broadcast', newValue);
    };

    supabase.broadcast.data.subscribe(callback);

    return () => {
      supabase.broadcast.data.unsubscribe(callback);
    };
  }, [dispatch, store]);

  return broadcast;
}

export default useInitBroadcast;
